import React from 'react';
import { GenericPage } from './GenericPage';
import { SignalRibbon } from '../components/Graphics';
export function Platform() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'The AI-native platform for autonomous exposure validation',
    subhead: 'Picus Platform converges Breach and Attack Simulation, Exposure Validation, and Automated Penetration Testing into one real-time, context-rich validation system. Picus Swarm extends that platform with governed agentic operations that reduce the manual gap between finding and action.',
    primaryCta: {
      label: 'Get a Demo',
      to: '/contact'
    },
    secondaryCta: {
      label: 'See Integrations',
      to: '/integrations'
    },
    graphic: <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mt-12" data-id="element-444">
            <div className="border border-picus-border bg-picus-surface p-8" data-id="element-445">
              <h3 className="text-picus-accent font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-446">
                Validation Motions
              </h3>
              <ul className="space-y-4 text-white" data-id="element-447">
                <li className="flex items-center gap-3" data-id="element-448">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-449" />{' '}
                  Breach and Attack Simulation
                </li>
                <li className="flex items-center gap-3" data-id="element-450">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-451" />{' '}
                  Exposure Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-452">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-453" />{' '}
                  Automated Penetration Testing
                </li>
                <li className="flex items-center gap-3" data-id="element-454">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-455" /> Picus
                  Swarm Workflow
                </li>
              </ul>
            </div>
            <div className="border border-picus-border bg-picus-surface p-8" data-id="element-456">
              <h3 className="text-picus-accent font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-457">
                Validation Domains
              </h3>
              <ul className="space-y-4 text-white" data-id="element-458">
                <li className="flex items-center gap-3" data-id="element-459">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-460" />{' '}
                  Security Control Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-461">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-462" /> Attack
                  Surface Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-463">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-464" /> Cloud
                  Security Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-465">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-466" /> Attack
                  Path Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-467">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-468" />{' '}
                  Detection Rule Validation
                </li>
              </ul>
            </div>
          </div>
  }} modules={[{
    title: 'Point tools create fragmented answers',
    subtitle: 'Security teams do not need another isolated signal. They need one platform that connects controls, exposures, exploitability, and action.',
    type: 'cards',
    cards: [{
      title: 'Disconnected Tools',
      description: 'Separate dashboards for scanners, BAS, and pen testing.'
    }, {
      title: 'Picus Platform',
      description: 'One unified system feeding a validated action stream.'
    }],
    graphic: 'Three disconnected tools on left feeding separate dashboards; one Picus Platform on right feeding a validated action stream.'
  }, {
    title: 'Autonomous exposure validation starts with context',
    subtitle: 'Anyone can run a fast script or automate a test. Picus Platform runs validation in the context of your real environment — your controls, your assets, your paths, your policies, your detections, and your workflow.',
    type: 'text',
    graphic: 'Context wheel around a validation core: controls, assets, identity, paths, policy, detections, business criticality.'
  }, {
    title: 'Validation works better when it works together',
    subtitle: 'BAS proves whether controls work. Exposure Validation proves what matters. Automated Penetration Testing proves exploitability and attack paths. Picus Platform brings those methods together.',
    type: 'custom',
    content: <SignalRibbon data-id="element-469" />
  }, {
    title: 'Picus Platform spans the full validation surface',
    subtitle: 'Picus Platform combines core validation motions with broad validation domains across the stack. That gives teams breadth, depth, and a common system for turning evidence into action.',
    type: 'text',
    graphic: '2-column matrix. Left: BAS, Exposure Validation, Automated Penetration Testing, Swarm. Right: Security Control Validation, Attack Surface Validation, Cloud Security Validation, Attack Path Validation, Detection Rule Validation.'
  }, {
    title: 'Turn signals into validated action',
    subtitle: 'Picus ingests signals from across the stack, validates what changed, and helps route the right next step. The result is less manual drag between finding and fix.',
    type: 'cards',
    cards: [{
      title: 'Signal',
      description: 'Ingest data from your environment.'
    }, {
      title: 'Validate',
      description: 'Test controls and exposures.'
    }, {
      title: 'Prioritize',
      description: 'Score based on real context.'
    }, {
      title: 'Route',
      description: 'Send to the right owner.'
    }],
    graphic: 'Signal → validate → prioritize → route → revalidate flow.'
  }, {
    title: 'Built to keep up with faster change',
    subtitle: 'The issue is not that humans are too slow. The issue is that manual handoffs, manual triage, and manual coordination do not scale as threats and environments change faster.',
    type: 'custom',
    content: <div className="mt-8" data-id="element-470">
              <a href="/platform/picus-swarm" className="inline-flex items-center px-6 py-3 border border-picus-border text-white rounded-full hover:bg-picus-surface transition-colors" data-id="element-471">
                Explore Picus Swarm
              </a>
            </div>,
    graphic: 'Manual-only workflow on left versus governed, signal-driven operating model on right.'
  }]} data-id="element-443" />;
}